import React from 'react';
import { useForm, ValidationError } from '@formspree/react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';
import './styles/Contact.css';

const ContactPage = styled.div`
  padding: 40px;
  font-family: 'Arial', sans-serif;
  background-color: var(--background-color);
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: background-color 0.3s ease;
`;

const ContactContainer = styled.div`
  max-width: 640px;
  width: 100%;
  margin: 0 auto;
  padding: 40px;
  background-color: var(--container-background-color);
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  transition: background-color 0.3s ease, box-shadow 0.3s ease;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Label = styled.label`
  font-weight: bold;
  margin-bottom: 4px;
`;

const Input = styled.input`
  padding: 12px;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.3s ease;
  &:focus {
    border-color: var(--primary-color);
    outline: none;
  }
`;

const TextArea = styled.textarea`
  padding: 12px;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-size: 16px;
  min-height: 150px;
  resize: vertical;
  transition: border-color 0.3s ease;
  &:focus {
    border-color: var(--primary-color);
    outline: none;
  }
`;

const Button = styled.button`
  padding: 12px 24px;
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  &:hover {
    background-color: #0056b3;
  }
  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const SuccessMessage = styled.p`
  color: green;
  text-align: center;
  font-weight: bold;
`;

const Contact = () => {
  const [state, handleSubmit] = useForm("xrbrgkwk");

  if (state.succeeded) {
    return (
      <ContactPage>
        <ContactContainer>
          <h1><span className="contact-icon">📞</span> Contactez-nous</h1>
          <SuccessMessage>Merci pour votre message ! Nous vous répondrons dès que possible.</SuccessMessage>
        </ContactContainer>
      </ContactPage>
    );
  }

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Contactez-nous | Les Z'ineuvitables - Théâtre à Neuville-en-Ferrain</title>
        <meta
          name="description"
          content="Contactez l'équipe des Z'ineuvitables pour toute question sur notre troupe de théâtre amateur à Neuville-en-Ferrain, nos événements, ou pour réserver des billets."
        />
        <meta
          name="keywords"
          content="contact, théâtre, Neuville-en-Ferrain, troupe amateur, Les Z'ineuvitables, événements, réservation, billetterie, salle Malraux, spectacles"
        />
        <meta property="og:title" content="Contactez-nous | Les Z'ineuvitables - Théâtre à Neuville-en-Ferrain" />
        <meta
          property="og:description"
          content="Contactez l'équipe des Z'Ineuvitables pour toute question sur notre troupe de théâtre amateur à Neuville-en-Ferrain, nos événements, ou pour réserver des billets."
        />
        <meta property="og:url" content="https://www.zineuvitables.org/contact" />
        <meta property="og:image" content="https://zineuvitables.org/logo.png" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="fr_FR" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contactez-nous | Les Z'ineuvitables - Théâtre à Neuville-en-Ferrain" />
        <meta
          name="twitter:description"
          content="Contactez l'équipe des Z'ineuvitables pour toute question sur notre troupe de théâtre amateur à Neuville-en-Ferrain, nos événements, ou pour réserver des billets."
        />
        <meta name="twitter:image" content="https://zineuvitables.org/logo.png" />
      </Helmet>
      <ContactPage>
        <ContactContainer className="contact-container">
          <h1><span className="contact-icon">📞</span> Contactez-nous</h1>
          <p>Pour toute question, suggestion ou réservation, remplissez le formulaire ci-dessous.</p>
          <Form onSubmit={handleSubmit}>
            <Label htmlFor="name">Votre nom</Label>
            <Input
              id="name"
              type="text"
              name="name"
              required
            />
            <ValidationError
              prefix="Nom"
              field="name"
              errors={state.errors}
            />

            <Label htmlFor="email">Votre email</Label>
            <Input
              id="email"
              type="email"
              name="email"
              required
            />
            <ValidationError
              prefix="Email"
              field="email"
              errors={state.errors}
            />

            <Label htmlFor="subject">Sujet</Label>
            <Input
              id="subject"
              type="text"
              name="subject"
              required
            />
            <ValidationError
              prefix="Sujet"
              field="subject"
              errors={state.errors}
            />

            <Label htmlFor="message">Votre message</Label>
            <TextArea
              id="message"
              name="message"
              required
            />
            <ValidationError
              prefix="Message"
              field="message"
              errors={state.errors}
            />

            <Button type="submit" disabled={state.submitting}>
              {state.submitting ? 'Envoi en cours...' : 'Envoyer'}
            </Button>
          </Form>
        </ContactContainer>
      </ContactPage>
    </>
  );
};

export default Contact;
